import { ProductItemInterface, VariantItem } from "@/assets/interfaces";
import RoundCheckbox from "@/components/ui/form-elements/round-checkbox";
import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import Badge from "@/components/ui/badge";
import { FormatCurrencyFun } from "@/contexts/cart-context-v2";
import { ImageOptionsStep, StateType, VariantsInternalStateItem } from "../modals/select-variant";
import SelectVariantExtra from "./select-variant-extra";

interface Props {
  step: ImageOptionsStep;
  setStep: (step: ImageOptionsStep) => void;
  variantOptions: VariantItem[];
  formatAsCurrency: FormatCurrencyFun;
  product: ProductItemInterface;
  optionKeys: string[];
  internalSelected: VariantsInternalStateItem[];
  setInternalSelected: React.Dispatch<React.SetStateAction<VariantsInternalStateItem[]>>;
  updateOptionState: (index: number, state: StateType) => void;
  updateOptionQuantity: (index: number, quantity: number) => void;
  deleteOption: (index: number) => void;
  updateOptionValues: (index: number, option: string, value: string) => void;
  saveSelections: (index: number) => void;
  addNewOption: () => void;
  selectedImages: string[];
  setSelectedImages: React.Dispatch<React.SetStateAction<string[]>>;
}

const SelectMultipleImageVariants = ({
  step,
  setStep,
  variantOptions,
  formatAsCurrency,
  product,
  optionKeys,
  internalSelected,
  updateOptionState,
  updateOptionQuantity,
  deleteOption,
  updateOptionValues,
  saveSelections,
  addNewOption,
  selectedImages,
  setSelectedImages,
  setInternalSelected,
}: Props) => {
  const toggleImageSelection = (image: string) => {
    setSelectedImages((prev) => {
      if (prev.includes(image)) {
        return prev.filter((i) => i !== image);
      }
      return [...prev, image];
    });
  };

  const imageIsSelected = (image: string) => selectedImages.includes(image);

  return (
    <>
      {step === ImageOptionsStep.START ? (
        <div className="grid items-start w-full grid-cols-[repeat(auto-fit,120px)] sm:grid-cols-[repeat(auto-fit,150px)] gap-2.5">
          {variantOptions.map((option, index) => (
            <figure
              className="relative w-full h-[120px] sm:h-[150px] rounded-10 overflow-hidden cursor-pointer"
              key={index}
              onClick={(e) => {
                option.available_to_purchase ? toggleImageSelection(option.image) : null;
              }}
            >
              <img src={option.image} alt="" className="w-full object-cover h-full" />

              <div className="absolute inset-0 bg-black bg-opacity-20 p-2.5">
                {option.available_to_purchase && (
                  <RoundCheckbox
                    name={`variant-option-image`}
                    id={option.image}
                    checked={imageIsSelected(option.image)}
                    small
                    disabled={!option.available_to_purchase}
                    onChange={() => (option.available_to_purchase ? toggleImageSelection(option.image) : null)}
                  />
                )}

                {!option.available_to_purchase && (
                  <span className="bg-white px-2 absolute text-accent-red-500 py-1 text-xxxs right-2.5 bottom-2.5 inline-flex font-semibold uppercase rounded-30">
                    Out of Stock
                  </span>
                )}
              </div>
            </figure>
          ))}
        </div>
      ) : (
        <SelectVariantExtra
          {...{
            selectedImages,
            options: product?.variants.options,
            extraOption: Object.keys(product?.variants?.options[0]?.values)[0],
            selected: internalSelected.map((s) => ({
              id: s.id,
              quantity: s.quantity,
              image: s.image,
              values: s.values,
              error: s.error,
            })),
            setSelected: (
              selected: {
                id: string;
                quantity: number;
                image: string;
                values: { [key: string]: string };
                state: StateType;
              }[]
            ) => setInternalSelected(selected.map((s) => ({ ...s, state: StateType.FORM, error: "" }))),
            formatAsCurrency,
            product,
            incrementQty: (id: string) => {
              const index = internalSelected.findIndex((s) => s.id === id);
              updateOptionQuantity(index, getQuantity(index, internalSelected) + 1);
            },
            decrementQty: (id: string) => {
              const index = internalSelected.findIndex((s) => s.id === id);
              const finalQuantity = getQuantity(index, internalSelected) - 1;

              if (finalQuantity > 0) {
                updateOptionQuantity(index, finalQuantity);
              } else {
                deleteOption(index);
              }
            },
            goBack: () => setStep(ImageOptionsStep.START),
            goForward: () => setStep(ImageOptionsStep.EXTRA),
            allImages: Array.from(new Set(variantOptions.map((o) => o.image))),
          }}
        />
      )}
    </>
  );
};

const getQuantity = (index: number, internalSelected: VariantsInternalStateItem[]) => {
  return internalSelected[index]?.quantity ?? 0;
};

export default SelectMultipleImageVariants;
